/**
 * First-run tutorial persistence — localStorage, following the same convention as
 * the other client-side prefs (settings, tool-config, fog).
 *
 * PER ACCOUNT, which it was not.
 *
 * The two keys used to be bare `tb-tutorial-done` / `tb-tutorial-stage`, shared by
 * every account that ever signed in on the browser. So the second account on a
 * machine — a QA account, a family member's, or just a player making a fresh
 * start — was handed a world with no tutorial at all: no character creator prompt,
 * no controls glade, no on-map tour, dropped straight into the real map. Every
 * other per-player key here is already scoped by email (`terraboma:appearance:…`,
 * `fog:…`, `terraboma:heroname:…`); these two simply missed it.
 *
 * Never a blocker: if storage is unavailable we treat the tutorial as already
 * seen, so a brand-new browser in private mode still gets straight into the game.
 */

/**
 * Whose progress we are reading and writing. Set from the router the moment the
 * signed-in player is known (main.ts), before the farm mounts.
 *
 * Empty until then. A read before that point falls back to the unscoped key,
 * which is also what makes the migration below work.
 */
let account = '';

/** Scope the tutorial keys to a player. Call on sign-in, before the farm mounts. */
export function setTutorialAccount(id: string): void {
  account = id || '';
  migrateLegacy();
}

const DONE_KEY = 'tb-tutorial-done';
const STAGE_KEY = 'tb-tutorial-stage';
const STEP_KEY = 'tb-tutorial-step';

/**
 * The ARENA tour's own seen-flag (tutorial/arena-tour.ts owns the tour itself).
 *
 * It lives here, not there, because "Replay tutorial" in Settings calls
 * resetTutorial() and the last card of the arena tour promises "Replay this guide
 * from Settings". Keeping the key next to the farm keys is what makes that promise
 * true — a reset that clears only the farm flags leaves the arena guide retired
 * for good.
 *
 * It is also SCOPED BY ACCOUNT now, like the three above. arena-tour.ts read and
 * wrote this key raw, so it had exactly the defect this whole module was written to
 * fix, one screen over: the second account on a browser walked into its first arena
 * — 25 fighters, lava, a sanctuary, a banking mechanic — with the guide already
 * retired by somebody else's play session.
 */
export const ARENA_DONE_KEY = 'tb-arena-tutorial-done';

const doneKey = () => (account ? `${DONE_KEY}:${account}` : DONE_KEY);
const stageKey = () => (account ? `${STAGE_KEY}:${account}` : STAGE_KEY);
const stepKey = () => (account ? `${STEP_KEY}:${account}` : STEP_KEY);
const arenaKey = () => (account ? `${ARENA_DONE_KEY}:${account}` : ARENA_DONE_KEY);

/**
 * One-time adoption of the old shared keys.
 *
 * Players who finished the tutorial before it was scoped have their progress
 * under the unscoped key. Ignoring it would replay the whole tutorial for
 * everybody on upgrade; keeping it forever would keep leaking one account's
 * progress to the next. So the FIRST account to sign in after the upgrade takes
 * the old value over — it is overwhelmingly likely to be the same person, on
 * their own browser — and the shared key is then removed, so the second account
 * starts clean like any new player.
 */
function migrateLegacy(): void {
  if (!account) return;
  try {
    const legacyDone = localStorage.getItem(DONE_KEY);
    const legacyStage = localStorage.getItem(STAGE_KEY);
    const legacyStep = localStorage.getItem(STEP_KEY);
    const legacyArena = localStorage.getItem(ARENA_DONE_KEY);
    if (legacyDone === null && legacyStage === null && legacyStep === null && legacyArena === null) return;
    if (localStorage.getItem(doneKey()) === null && legacyDone !== null) {
      localStorage.setItem(doneKey(), legacyDone);
    }
    if (localStorage.getItem(stageKey()) === null && legacyStage !== null) {
      localStorage.setItem(stageKey(), legacyStage);
    }
    if (localStorage.getItem(stepKey()) === null && legacyStep !== null) {
      localStorage.setItem(stepKey(), legacyStep);
    }
    if (localStorage.getItem(arenaKey()) === null && legacyArena !== null) {
      localStorage.setItem(arenaKey(), legacyArena);
    }
    localStorage.removeItem(DONE_KEY);
    localStorage.removeItem(STAGE_KEY);
    localStorage.removeItem(STEP_KEY);
    localStorage.removeItem(ARENA_DONE_KEY);
  } catch { /* private mode — nothing to migrate into */ }
}

export function tutorialSeen(): boolean {
  try { return localStorage.getItem(doneKey()) === '1'; } catch { return true; }
}

export function markTutorialDone(): void {
  try { localStorage.setItem(doneKey(), '1'); } catch { /* private mode — no-op */ }
}

/**
 * Settings "Replay tutorial" clears the flags so the next farm load re-runs it —
 * and the arena flag with them, so the next match replays that guide too.
 */
export function resetTutorial(): void {
  try {
    localStorage.removeItem(doneKey());
    localStorage.removeItem(stageKey());
    localStorage.removeItem(stepKey());
    localStorage.removeItem(arenaKey());
    // The unscoped key too: a player who never signed in again after the upgrade
    // still has their old value sitting there, and a "Replay tutorial" that leaves
    // it behind would be a promise the arena quietly breaks.
    localStorage.removeItem(ARENA_DONE_KEY);
  } catch { /* no-op */ }
}

// ─── The ARENA tour's flag (the tour itself is tutorial/arena-tour.ts) ───────
/**
 * Storage failure reads as "already seen", the same rule the farm flags follow — a
 * browser in private mode must never have a tutorial forced on it every match.
 */
export function arenaTutorialSeen(): boolean {
  try { return localStorage.getItem(arenaKey()) === '1'; } catch { return true; }
}

export function markArenaTutorialDone(): void {
  try { localStorage.setItem(arenaKey(), '1'); } catch { /* private mode — no-op */ }
}

/** Settings / lobby "replay" — the next match runs the arena tour again. */
export function resetArenaTutorial(): void {
  try { localStorage.removeItem(arenaKey()); localStorage.removeItem(ARENA_DONE_KEY); } catch { /* no-op */ }
}

// ─── Two-phase progress: the controls SANDBOX, then the on-map TOUR ──────────
export type TutorialStage = 'sandbox' | 'tour' | 'done';

export function tutorialStage(): TutorialStage {
  try {
    const s = localStorage.getItem(stageKey());
    if (s === 'sandbox' || s === 'tour' || s === 'done') return s;
    // No stage yet: a returning player who already finished is 'done', a brand
    // new one starts in the sandbox.
    return tutorialSeen() ? 'done' : 'sandbox';
  } catch { return 'done'; }
}

export function setTutorialStage(s: TutorialStage): void {
  try {
    // A step index only means anything WITHIN a stage — the glade has 7 steps and
    // the on-map tour 13. Leaving the index alone across the sandbox→tour handoff
    // would open the tour at the glade's step and skip half the lesson, so a real
    // stage change zeroes it. Re-writing the stage the player is ALREADY in must
    // not, or a checkpoint write would wipe the resume point it just restored.
    // Compared against the EFFECTIVE stage, not the raw key: with no key stored a
    // new player is already in the glade, so the first explicit write of
    // 'sandbox' is not a change and must leave the step where it is.
    if (tutorialStage() !== s) localStorage.setItem(stepKey(), '0');
    localStorage.setItem(stageKey(), s);
    if (s === 'done') localStorage.setItem(doneKey(), '1');
  } catch { /* private mode */ }
}

// ─── Step index within the current stage ────────────────────────────────────
/**
 * Which step of the current stage the player is on.
 *
 * Only the STAGE used to survive a reload, so refreshing during the glade or the
 * tour threw the player back to "Welcome to Terraboma" — and the tour's chop/mine
 * steps re-baseline on entry, so they were made to fell a tree they had already
 * felled. The coach now writes this on every advance and seeds itself from it.
 *
 * Anything unparseable, negative or missing reads as 0: degrading to today's
 * restart-from-the-top is fine, wedging the farm is not.
 */
export function tutorialStep(): number {
  try {
    const n = Number.parseInt(localStorage.getItem(stepKey()) ?? '', 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch { return 0; }
}

export function setTutorialStep(i: number): void {
  try { localStorage.setItem(stepKey(), String(Math.max(0, Math.floor(i)))); } catch { /* private mode — no-op */ }
}

/** Test seam: forget which account is scoped. */
export function __resetTutorialAccount(): void { account = ''; }
